import { useDrawingArea, useXScale, useYScale } from '@mui/x-charts/hooks';
import { area, curveNatural, CurveFactory } from '@mui/x-charts-vendor/d3-shape';
import React, { useId } from 'react';

// Opacity at the baseline when fade is on: close to invisible, so small gaps stay quiet.
const FADED_OPACITY = 0.02;

type BenchmarkGapBandProps = {
  points: string[];
  values: (number | null)[];
  baseline: (number | null)[];
  aboveColor: string;
  belowColor: string;
  opacity?: number;
  // Fades the fill toward the baseline instead of a flat fill.
  fade?: boolean;
  // Must match the curve of the lines it sits between, or the band edges drift off them. MUI's 'natural' is curveNatural.
  curve?: CurveFactory;
};

// Shades the gap between a series and its baseline: aboveColor where values sit above the baseline, belowColor where below.
const BenchmarkGapBand: React.FC<BenchmarkGapBandProps> = ({
  points,
  values,
  baseline,
  aboveColor,
  belowColor,
  opacity = 0.32,
  fade = false,
  curve = curveNatural,
}) => {
  const id = useId().replace(/:/g, '');
  const xScale = useXScale<'point'>();
  const yScale = useYScale<'linear'>();
  const { top, height } = useDrawingArea();

  const x = (index: number) => xScale(points[index]) ?? 0;
  const y = (series: (number | null)[]) => (index: number) => yScale(series[index] ?? 0) ?? 0;
  const withValue = (series: (number | null)[]) => points.map((_point, index) => index).filter((index) => series[index] !== null);

  // Each area runs through every point of its own series, exactly like the line drawn for it. Where the two overlap is
  // the gap between the lines, even in periods where only one series has a value.
  const valueIndexes = withValue(values);
  const baselineIndexes = withValue(baseline);
  const edgeArea = (series: (number | null)[], indexes: number[], edge: number) =>
    area<number>().x(x).y0(edge).y1(y(series)).curve(curve)(indexes);
  const aboveValues = edgeArea(values, valueIndexes, top);
  const belowValues = edgeArea(values, valueIndexes, top + height);
  const aboveBaseline = edgeArea(baseline, baselineIndexes, top);
  const belowBaseline = edgeArea(baseline, baselineIndexes, top + height);

  // The band only spans periods both series cover, from the first matched period to the last.
  const matched = valueIndexes.filter((index) => baseline[index] !== null);
  const spanStart = x(matched[0]);
  const spanEnd = x(matched[matched.length - 1]);

  const edgeOpacity = fade ? FADED_OPACITY : opacity;

  if (matched.length < 2 || !aboveValues || !belowValues || !aboveBaseline || !belowBaseline) return null;

  return (
    <g pointerEvents="none">
      <defs>
        <clipPath id={`${id}-span`}>
          <rect x={spanStart} y={top} width={spanEnd - spanStart} height={height} />
        </clipPath>
        <clipPath id={`${id}-above`}>
          <path d={aboveBaseline} />
        </clipPath>
        <clipPath id={`${id}-below`}>
          <path d={belowBaseline} />
        </clipPath>
        <linearGradient id={`${id}-above-fill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={aboveColor} stopOpacity={opacity} />
          <stop offset="100%" stopColor={aboveColor} stopOpacity={edgeOpacity} />
        </linearGradient>
        <linearGradient id={`${id}-below-fill`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={belowColor} stopOpacity={edgeOpacity} />
          <stop offset="100%" stopColor={belowColor} stopOpacity={opacity} />
        </linearGradient>
      </defs>
      <g clipPath={`url(#${id}-span)`}>
        {/* Below the value line and above the baseline: values lead. */}
        <g clipPath={`url(#${id}-above)`}>
          <path d={belowValues} fill={`url(#${id}-above-fill)`} />
        </g>
        {/* Above the value line and below the baseline: values trail. */}
        <g clipPath={`url(#${id}-below)`}>
          <path d={aboveValues} fill={`url(#${id}-below-fill)`} />
        </g>
      </g>
    </g>
  );
};

export default BenchmarkGapBand;
